// @flow

import _ from 'lodash';

import { pluralize } from '@zapier/common-utils';

import * as Plan from 'app/entities/Plan';
import NodeUtils from 'app/beta-editor/utils/NodeUtils';
import ServiceUtils from 'app/service/ServiceUtils';
import { addTruthyParamsToUrl } from 'app/common/utils/UrlUtils';
import { exists } from 'app/common/CommonUtils';
import { getNodeCrumbs } from 'app/beta-editor/utils/TaskStateUtils';
import {
  HELD_STATUS,
  HELD_STATUS_ICONS,
  HELD_STATUS_LABELS,
  HeldReasons,
  PLAN_LIMIT_HELD_REASONS,
  PREMIUM_APP,
  RUNNING_STATUS_LABELS,
  STATUS_LABELS,
  SUB_STATUS_ICONS,
  SUB_STATUS_LABELS,
} from 'app/history/utils/constants';
import {
  findNodeUpwards,
  getParentId,
  sortNodes,
} from 'app/beta-editor/utils/NodeTrees';
import {
  getAllPathNodesForZap,
  getPathName,
  isPathNode,
  isPathsGroupNode,
} from 'app/beta-editor/utils/PathUtils';

import type {
  FlowNode,
  MaybeSkinnyFlow,
  NodeMap,
  SkinnyNode,
} from 'app/beta-editor/flowState';
import type { ID } from 'app/common/types';
import type { Service, ServicesMap } from 'app/service/types';
import type { Task, TaskHistoryStep } from 'app/history/types';

export const PARAM_KEYS = [
  'page',
  'start_datetime',
  'end_datetime',
  'query',
  'root_id',
  'status',
  'attempt',
];

export const cleanQueryParams = function(currentQueryParams: {}) {
  return _.pick(currentQueryParams, PARAM_KEYS);
};

export const getTaskTitle = function(task: Task, zap?: MaybeSkinnyFlow) {
  if (zap) {
    return zap.title || task.object_title || NodeUtils.EMPTY_ZAP_NAME;
  }
  return (task.object_title || NodeUtils.EMPTY_ZAP_NAME) + ' (Zap Deleted)';
};

const TYPE_OF_LABEL = {
  read: 'trigger',
  write: 'action',
  search: 'search',
  search_or_write: 'lookup or action',
};

const convertTypeof = function(typeOf) {
  return TYPE_OF_LABEL[typeOf] || typeOf;
};
const isTooMany = (task: Task) => task.held_reason === HeldReasons.TOO_MANY;
const isBlacklistedApp = (task: Task) =>
  task.held_reason === HeldReasons.BLACKLISTED_APP;
const isHeldBecauseStaleAuth = (task: Task) =>
  task.held_reason === HeldReasons.STALE_AUTH;
const isDedupeChange = (task: Task) =>
  task.held_reason === HeldReasons.DEDUPE_CHANGE;

export const isPlanLimitHeldReason = (reason: string) =>
  _.includes(PLAN_LIMIT_HELD_REASONS, reason);

export const isHeldBecausePaymentIssue = (task: Task) =>
  task.held_reason === HeldReasons.PAYMENT_ISSUE;

const allowedReplayStatuses = [
  'halted',
  'error',
  'filtered',
  'skipped',
  'pending',
  'scheduled',
  'delayed',
  'throttled',
];
const canReplayBufferAfterMs = 10 * 60 * 1000; // 10 minutes
const canReplayScheduledAfterMs = (10 * 60 + 35) * 60 * 1000; // 10 hours, 35 minutes
const cantReplayBefore = 84 * 24 * 60 * 60 * 1000; // 84 days (~3 months - from server side limit)

const stamp = str => (str ? new Date(str) : new Date()).getTime();

export const canTaskReplay = function(task: Task) {
  if (task.status === 'success') {
    // Can't replay success tasks
    return false;
  }

  const nowStamp = stamp();
  const startStamp = stamp(task.start_time);

  if (nowStamp - startStamp > cantReplayBefore) {
    return false;
  }

  let isWaitTimeElapsed = true; // applies to statuses that should change over time ie pending/scheduled
  if (task.status === 'pending') {
    // can replay pending after time delta
    isWaitTimeElapsed = nowStamp - startStamp > canReplayBufferAfterMs;
  }

  if (task.status === 'scheduled') {
    // can replay scheduled after time delta
    isWaitTimeElapsed = nowStamp - startStamp > canReplayScheduledAfterMs;
  }

  if (task.status === 'delayed') {
    // can replay delayed tasks after some time after release
    const delayedNodeId = _.invert(task.statuses).delayed;
    const releaseAt = (task.output[delayedNodeId] || {}).release_at;
    if (!releaseAt) {
      // task history list view has skinny payloads, no task.output - hide replay option for safety's sake
      // conceivably we could let people try since the backend has safety checks
      return false;
    }
    const releaseStamp = stamp(releaseAt);
    isWaitTimeElapsed = nowStamp - releaseStamp > canReplayBufferAfterMs;
  }

  if (task.status === 'held') {
    return true;
  }

  return isWaitTimeElapsed && _.includes(allowedReplayStatuses, task.status);
};

// Filter out only the nodes which we know are in this
// particular path. This includes the path node, any child nodes,
// and finally the branching node with all its child paths.
// we're building a tree from the top down, navigating by children
export const getPathSpecificNodes = (
  startingNodeId: ID,
  nodes: { [nodeId: ID]: FlowNode },
  nodesList: Array<SkinnyNode> = []
) => {
  const parentNode = nodes[startingNodeId];
  nodesList = [...nodesList, parentNode];

  const nextChildren = _.filter(nodes, node => {
    return getParentId(node) === Number(startingNodeId);
  });
  if (_.isEmpty(nextChildren)) {
    return nodesList;
  } else {
    return nextChildren.reduce((acc, nextChild) => {
      return getPathSpecificNodes(nextChild.id, nodes, acc);
    }, nodesList);
  }
};

// This util takes in a task and builds a tree of all the task IDs
// to their node ID. It does this by starting from the top level task,
// and looking in the child_forks, which are a map of nodeId to taskId.
// It maps those child_forks by their task Id to node Id (reversing them)
// and then finds any of their child_forks. It keeps digging down
// the tree until it has found all of the child_forks and finally
// returns a map of all the tasks in the tree mapped to their node id.
export const getTaskIdToNodeIdMap = (
  task: Task,
  taskNodeMap: {
    [taskId: string]: number,
  } = {}
) => {
  const { child_forks, child_task_map } = task;
  if (!child_forks || !child_task_map) {
    return taskNodeMap;
  } else {
    // find the children's node ids and add them to the map
    // transform the child_forks, which is nodeId: taskId
    // into an object which is easier to query for the specific task id:
    // taskId: nodeID
    const mapWithChildForks = _.reduce(
      child_forks,
      (map, taskId, nodeId) => {
        taskNodeMap[taskId] = Number(nodeId);
        return taskNodeMap;
      },
      taskNodeMap
    );
    // then we need to find the forks in the child_task_map and go through
    // those tasks, looking at the child_forks and adding those to the taskNodeMap.
    return _.reduce(
      child_forks,
      (map, childForkId) => {
        const childTaskMap = task.child_task_map || {};
        const childTask = childTaskMap[childForkId];
        if (!childTask) {
          return map;
        } else {
          // add the child task map here from the main parent task
          // so that we can continue to look up the child tasks
          return getTaskIdToNodeIdMap({ ...childTask, child_task_map }, map);
        }
      },
      mapWithChildForks
    );
  }
};

// takes in a map of taskId to nodeId and reverses it
// so that we have nodeId to taskId.
export const reverseTaskIdNodeMap = (taskIdNodeMap: {
  [taskId: string]: number,
}) => _.invert(taskIdNodeMap);

export const getPathTask = ({
  task,
  pathId,
  nodeId,
}: {
  task: Task,
  pathId?: string,
  nodeId?: number | ID,
}) => {
  if (nodeId && task.child_task_map) {
    // if we're looking for a specific node id, find that path
    const taskMap = {
      ...task.child_task_map,
      [task.fork]: task,
    };
    const taskForNode = _.find(taskMap, tsk => {
      return tsk.output.hasOwnProperty(nodeId);
    });
    return taskForNode || task;
  } else if (pathId) {
    return _.get(task, ['child_task_map', pathId], task);
  }
  return task;
};

/* Take a task and return "steps" that represent a UI construct for the details page. */
export const getTaskDetailedSteps = (
  task: Task,
  pathId?: ID,
  nodeId?: ID | number
) => {
  let isFiltered = false;
  let filteredCount = 0;
  const taskIdNodeMap = getTaskIdToNodeIdMap(task);
  // if we are on a path step, then we need to get the task for
  // that path. It may be a child of the parent task, so the
  // data for the path task won't be found in the parent task.
  const maybeSubTask: Task = getPathTask({ task, pathId, nodeId });
  const subTaskNodeId = pathId ? taskIdNodeMap[pathId] : null;
  // if we are on a sub step, we only want to get the nodes that are
  // specific to that path. Otherwise, get all the steps in order.
  const nodesForSteps = subTaskNodeId
    ? // really goofy that we have to do this because indexing an object
      // with a number converts it to a string. But flow is mad
      // because the values in this object are numbers, so lets just convert it
      // to a string here.
      getPathSpecificNodes(String(subTaskNodeId), task.zap)
    : sortNodes(task.zap);
  return _.chain(nodesForSteps)
    .map(node => {
      const sample = NodeUtils.sample(node, maybeSubTask);
      const breadcrumbs = getNodeCrumbs(maybeSubTask);
      const lastBreadcrumb = _.last(
        _.filter(breadcrumbs, { object_id: node.id })
      );
      const step = {
        status: sample.status,
        sample,
        node,
        time: (lastBreadcrumb || {}).start_time,
        typeLabel: convertTypeof(node.type_of),
      };
      if (step.status === 'filtered') {
        const isFilterNode = _.get(step, ['node', 'type_of']) === 'filter';
        const skippedNode = {
          ...step,
          filteredCount,
          status: isFiltered ? 'skipped' : step.status,
        };
        filteredCount += 1;
        if (isFiltered) {
          // isFiltered so we only insert one "filter" ahead
          // otherwise we'd convert raw "trigger -> filtered -> filtered"
          // to "trigger -> filtered -> skipped -> filtered -> skipped"
          // we want "trigger -> filtered -> skipped -> skipped"
          return [skippedNode];
        } else {
          isFiltered = true;
          filteredCount += 1;
          const fauxFilterNode = isFilterNode
            ? null
            : _.extend({}, step, { filteredCount: filteredCount - 1 });
          return [fauxFilterNode, skippedNode];
        }
      }
      return [step];
    })
    .flatten()
    .compact()
    .value();
};

// A util to see if the paths nested within a task ran or not.
// If this is a path-within-a-path, then it is necessary
// to pass the task of the immediate parent, rather than the root
// parent fork. I.e. if the tree is Root -> Path A -> Path C,
// you need to be sure to path the task for Path C rather than
// for the root.
export const didPathRun = (task: Task, path: TaskHistoryStep) => {
  return task.statuses[path.node.id] === 'success';
};

/*
  Give a nice human readable description for a step.

    "Found 1 new Item Via Poll in Tester"

*/

// this will find the nodeId which represents
// the node that caused the step not to run.
// it could be a path or a filter node.
const findFilteringNodeId = (ruleId, output) => {
  return _.reduce(
    output,
    (result: ?ID, outputItem: {}, nodeId: ID) => {
      const canContinue = _.get(outputItem, ['can_continue']);
      const zapFilterData = _.get(outputItem, ['_zap_data_filter_meta']);
      const filterMetaMatchesRuleId = _.some(
        zapFilterData,
        datum => datum.id === ruleId
      );
      if (!canContinue && filterMetaMatchesRuleId) {
        return nodeId;
      }
      return result;
    },
    undefined
  );
};

const wasFilterOrPath = (task: Task) => {
  const filteringNodeId = findFilteringNodeId(
    _.head(_.get(task, ['why_stop', 'rule_ids'])),
    task.output
  );
  const filteringNode = filteringNodeId && task.zap[filteringNodeId];
  return filteringNode && isPathNode(filteringNode) ? 'path' : 'filter';
};

type getStepDescriptionProps = {
  step: TaskHistoryStep,
  task: Task,
  service: Service,
  pathId?: string,
  nodeId?: number,
  isOnSubPath?: boolean,
};

export const getStepDescription = ({
  step,
  task,
  service = { name: 'your app' },
  pathId,
  nodeId,
  isOnSubPath,
}: getStepDescriptionProps) => {
  const maybeSubTask = getPathTask({ task, pathId, nodeId });
  const serviceFormatted = {
    ...service,
    name: service.name ? service.name.replace(' by Zapier', '') : 'your app',
  };

  const node = step.node;
  const { name: serviceName } = serviceFormatted;
  const actionNoun =
    ServiceUtils.actionName(service, node.action, node.type_of, {
      hasNoVerb: true,
    }) || 'Item';

  if (step.status === 'scheduled') {
    return `This ${serviceName} step has been rescheduled in ${maybeSubTask.time_to_rescheduled ||
      'the future'}.`;
  }

  if (step.status === 'error' && maybeSubTask.status === 'scheduled') {
    return `${serviceName} hit an error, so the task has been rescheduled in ${maybeSubTask.time_to_rescheduled ||
      'the future'}.`;
  }

  if (step.status === 'pending') {
    return `This ${serviceName} step hasn't been run yet.`;
  }

  if (step.status === 'delayed') {
    return `This ${serviceName} step has been delayed.`;
  }
  if (step.status === 'held') {
    let heldReason = 'of unknown reason';
    if (isTooMany(maybeSubTask)) {
      heldReason = 'it returned too many items at once';
    } else if (isDedupeChange(maybeSubTask)) {
      heldReason = 'its deduplication key changed';
    } else if (isHeldBecauseStaleAuth(maybeSubTask)) {
      heldReason = 'its account needs reconnection';
    } else if (isPlanLimitHeldReason(maybeSubTask.held_reason)) {
      heldReason = 'you’ve met your plan limit';
    } else if (isHeldBecausePaymentIssue(maybeSubTask)) {
      heldReason = 'of a payment issue';
    } else if (isBlacklistedApp(maybeSubTask)) {
      heldReason = 'the app was restricted by an administrator';
    }
    return `This ${serviceName} step was held because ${heldReason}.`;
  }

  // handle by types...

  if (node.type_of === 'read') {
    return `Found 1 new ${actionNoun} in ${serviceName}.`;
  }

  if (node.type_of === 'search') {
    if (step.status === 'success') {
      return `Found ${actionNoun} in ${serviceName}.`;
    } else if (step.status === 'skipped' && step.filteredCount) {
      const filterOrPath = wasFilterOrPath(maybeSubTask);
      return `${actionNoun} not searched in ${serviceName}. A ${filterOrPath} stopped this step from running.`;
    } else if (step.status === 'halted') {
      return `This step was safely halted because nothing was found in ${serviceName}.`;
    }

    if (!step.status) {
      return `Did not attempt to find ${actionNoun} in ${serviceName}.`;
    }
  }

  if (isPathsGroupNode(step.node)) {
    if (step.status === 'success') {
      const branchesThatRan = _.filter(step.childPaths, childPath => {
        const childTask = getPathTask({ task, nodeId: childPath.node.id });
        return didPathRun(childTask, childPath);
      });
      const numBranchesThatRan = branchesThatRan.length;
      return `Ran ${numBranchesThatRan} ${pluralize(
        'Path',
        numBranchesThatRan
      )}.`;
    } else {
      return `Did not run any Paths.`;
    }
  }

  if (node.type_of === 'write') {
    if (step.status === 'success') {
      return `Sent 1 new ${actionNoun} to ${serviceName}.`;
    } else if (
      step.status === 'filtered' &&
      step.filteredCount &&
      maybeSubTask.why_stop &&
      maybeSubTask.why_stop.rule_dumps
    ) {
      const plural = maybeSubTask.why_stop.rule_dumps.length > 1;
      return `${
        plural ? 'These filters' : 'This filter'
      } stopped this Zap from running.`;
    } else if (step.status === 'skipped' && step.filteredCount) {
      const filterOrPath = wasFilterOrPath(maybeSubTask);
      return `${actionNoun} not sent to ${serviceName}. A ${filterOrPath} stopped this step from running.`;
    }

    if (!step.status) {
      return `Did not attempt to send new ${actionNoun} in ${serviceName}.`;
    }
  }

  if (node.type_of === 'search_or_write') {
    // TODO! need to grab related objects...
    return `Found or created ${actionNoun} in ${serviceName}.`;
  }

  if (node.type_of === 'filter' && !isPathNode(node)) {
    if (step.status === 'success') {
      return `Your task passed the rules for this filter.`;
    } else if (step.status === 'filtered') {
      return `This filter successfully stopped your task.`;
    } else if (!step.status) {
      return `Your task is pending—we haven’t tried this filter yet.`;
    }
  }

  if (isPathNode(node)) {
    const pathName = getPathName(node);

    if (step.status === 'success') {
      return `${pathName} (Condition Matched)`;
    } else if (step.status === 'filtered') {
      return `${pathName} (Condition Did Not Match)`;
    } else if (!step.status) {
      return `Your task is pending—we haven’t tried this filter yet.`;
    }
  }

  // everything below here is designed as fallbacks

  if (step.status === 'error') {
    return `This ${serviceName} step hit an error.`;
  }

  if (step.status === 'halted') {
    return `This ${serviceName} step was safely halted.`;
  }

  if (step.status === 'throttled') {
    return `This ${serviceName} step was throttled by Zapier.`;
  }

  if (step.status === 'skipped') {
    const maybeNotInPathMessage = isOnSubPath
      ? 'That step may not be in this Path.'
      : '';
    return `This ${serviceName} step didn‘t run because it depended on a previous step or filter that wasn‘t successful. ${maybeNotInPathMessage}`;
  }

  if (step.status === 'delayed') {
    return `This ${serviceName} step has been delayed.`; // "until TODO"
  }

  return `This ${serviceName} step was ${step.status}.`;
};

// Silly parsing of strings like 'Required field "Normal Field" (unicodefield) is missing.'
export const extractMissingKeyFromErrorMessage = (errorMessage: ?string) => {
  if (
    errorMessage &&
    errorMessage.indexOf('Required field "') === 0 &&
    errorMessage.indexOf(') is missing.') >= 16
  ) {
    const startOfKey = errorMessage.lastIndexOf('" (') + '" ('.length; // my my this is fragile!
    const endOfKey = errorMessage.indexOf(') is missing.');
    return errorMessage.slice(startOfKey, endOfKey);
  }
  return undefined;
};

export const getZapEditUrl = function(zap?: MaybeSkinnyFlow) {
  if (!zap) {
    return '';
  }

  return `/app/editor/${zap.id}`;
};

export const getZapEditStepUrl = function(
  zap?: MaybeSkinnyFlow,
  step: TaskHistoryStep
) {
  if (!zap) {
    return '';
  }

  return `/app/editor/${zap.id}/nodes/${step.node.id}/setup`;
};

export const getTaskHistoryForZapUrl = (zap?: MaybeSkinnyFlow) => {
  if (!zap) {
    return '';
  }

  return addTruthyParamsToUrl('/app/history', {
    root_id: zap.id,
  });
};

// Returns true if the task failed because of a stale auth, but the auth is
// no longer stale. This happens when the user has reconnected the auth,
// but has not yet replayed ('released') the held task.
const isNoLongerStale = task =>
  task.status === HELD_STATUS &&
  isHeldBecauseStaleAuth(task) &&
  !task.isStaleAuth;

const isUnlimited = value => value === -1;

const PLAN_LIMIT_PROPS = {
  task_limit: 'actions',
  step_limit: 'zap_length',
  blacklisted_app: 'allow_premium_apps',
};

export const hasSufficientlyUpgraded = (
  heldReason: string,
  heldPlanLimit: string,
  currentPlan: {}
) => {
  if (!isPlanLimitHeldReason(heldReason)) {
    return false; // not relevant
  }

  const parsedValue =
    heldReason === PREMIUM_APP ? heldPlanLimit : parseInt(heldPlanLimit);
  const currentPlanLimit = Plan.selectors.dataItem(
    PLAN_LIMIT_PROPS[heldReason],
    currentPlan
  );

  if (heldReason === PREMIUM_APP) {
    return !!currentPlanLimit;
  }
  return (
    isUnlimited(currentPlanLimit) ||
    isUnlimited(parsedValue) ||
    currentPlanLimit > parsedValue
  );
};

export const hasSufficientlyUpgradedForTask = (task: Task, currentPlan: {}) => {
  if (!task.held_plan_limit || task.status !== HELD_STATUS) {
    return false; // not relevant
  }

  const [limitName, value] = task.held_plan_limit.split(':');
  return hasSufficientlyUpgraded(limitName, value, currentPlan);
};

export const hasBeenResolved = (task: Task, currentPlan: {}) => {
  if (task.held_reason === HeldReasons.PAYMENT_ISSUE) {
    return false;
  }

  return (
    task.isDeleted ||
    isNoLongerStale(task) ||
    hasSufficientlyUpgradedForTask(task, currentPlan)
  );
};

export const getTaskStatusIcon = (task: Task, currentPlan: {}) => {
  if (hasBeenResolved(task, currentPlan)) {
    return null;
  }

  if (task.status === HELD_STATUS) {
    return HELD_STATUS_ICONS[task.held_reason];
  }
  return SUB_STATUS_ICONS[task.status];
};

export const getTaskSubStatusLabel = (
  task: Task,
  services: ServicesMap,
  currentPlan: {}
) => {
  if (hasBeenResolved(task, currentPlan)) {
    return '';
  }
  if (task.status === HELD_STATUS) {
    if (isHeldBecauseStaleAuth(task)) {
      const serviceName = ServiceUtils.nameFromApi(task.staleAuthApi, services);
      return `Reconnect ${serviceName}`;
    }

    return HELD_STATUS_LABELS[task.held_reason];
  }

  return SUB_STATUS_LABELS[task.status];
};

export const getTaskStatusLabel = ({
  status,
  isRunning,
  massActionName,
}: {
  status: string,
  isRunning?: boolean,
  massActionName: ?string,
}) => {
  if (isRunning && massActionName) {
    return RUNNING_STATUS_LABELS[massActionName];
  }
  return STATUS_LABELS[status];
};

export const getSelectionDetails = ({
  isAllTasksSelected,
  numSelected,
  numberOfReplayableTasks,
  numberOfTotalTasks,
}: {
  isAllTasksSelected: boolean,
  numSelected: number,
  numberOfReplayableTasks: number,
  numberOfTotalTasks: number,
}) => {
  if (isAllTasksSelected && numberOfTotalTasks > numberOfReplayableTasks) {
    const maxSelectableTasks = numberOfReplayableTasks.toLocaleString();
    return `You can choose up to ${maxSelectableTasks} tasks at a time. ${maxSelectableTasks} of ${numberOfTotalTasks.toLocaleString()} selected.`;
  }

  if (numSelected < numberOfReplayableTasks) {
    return `${numSelected.toLocaleString()} on the page are selected.`;
  }

  // When numSelected === numberOfReplayableTasks (applies to both select all and select shown)
  return `${numSelected.toLocaleString()} of ${numberOfReplayableTasks.toLocaleString()} tasks are selected.`;
};

export const getNumSelected = (selectedTasks: Object) =>
  _.filter(selectedTasks).length;

const hasSelectedMaxTasks = (
  numSelected: number,
  numberOfReplayableTasks: number
) => numberOfReplayableTasks > 0 && numSelected === numberOfReplayableTasks;

const hasSelectedAllVisibleTasks = (
  numSelected: number,
  tasksPerPage: number
) => numSelected === tasksPerPage;

export const shouldShowSelectionDetails = ({
  numSelected,
  tasksPerPage,
  numberOfReplayableTasks,
  numberOfTotalTasks,
  isAllTasksSelected,
}: {
  isAllTasksSelected: boolean,
  numSelected: number,
  tasksPerPage: number,
  numberOfReplayableTasks: number,
  numberOfTotalTasks: number,
}) =>
  (!isAllTasksSelected &&
    hasSelectedAllVisibleTasks(numSelected, tasksPerPage)) ||
  hasSelectedMaxTasks(numSelected, numberOfReplayableTasks) ||
  (isAllTasksSelected && numberOfTotalTasks > numberOfReplayableTasks);

/*
   Returns true if any status for a 'task' (fork really)
   are pending.
*/
export const isRunning = (task: Task) =>
  _.values(task.statuses).some(status => status === 'pending');

export const hasSelectedTasks = (selectedTasks: {}) =>
  !_.isEmpty(_.compact(_.values(selectedTasks)));

type TaskHistoryProps = {
  tasksPerPage: number,
  page: number,
  numberOfReplayableTasks: number,
  deletedTaskIds: string[],
  task: { id: string },
  index: number,
};

export const isTaskSelectedOnSelectAll = ({
  tasksPerPage,
  page,
  numberOfReplayableTasks,
  deletedTaskIds,
  task,
  index,
}: TaskHistoryProps) => {
  const taskNum = tasksPerPage * (page - 1) + index + 1;

  const isTaskDeleted = _.includes(deletedTaskIds, task.id);
  return !isTaskDeleted && taskNum <= numberOfReplayableTasks;
};

export const taskHasPaths = (task: Task) =>
  exists(task.child_forks) && getAllPathNodesForZap(task.zap).length > 0;

export const isParentOfStep = (
  maybeParentStep: TaskHistoryStep,
  currentStep: TaskHistoryStep
) => {
  return currentStep.node.parent === maybeParentStep.node.id;
};

export const isStepInPath = (
  step: TaskHistoryStep,
  taskSteps: TaskHistoryStep[]
) => {
  // go up the tree until you find a paths group node.
  // if you never find a paths group node, then return false.
  const parentId = getParentId(step.node);
  const parentStep = exists(parentId)
    ? _.find(taskSteps, taskStep => taskStep.node.id === parentId)
    : null;
  if (!exists(parentId) || !parentStep) {
    return false;
  } else if (parentStep && isPathsGroupNode(parentStep.node)) {
    return true;
  } else {
    return isStepInPath(parentStep, taskSteps);
  }
};

export const sortChildrenAndNonChildren = (
  step: TaskHistoryStep,
  maybeChildren: TaskHistoryStep[]
) => {
  return _.partition(maybeChildren, maybeChild => {
    return isParentOfStep(step, maybeChild);
  });
};

type SortedStepObject = {
  pathSteps: TaskHistoryStep[],
  pathGroupSteps: TaskHistoryStep[],
  normalSteps: TaskHistoryStep[],
};

const sortedStepObject: SortedStepObject = {
  pathSteps: [],
  normalSteps: [],
  pathGroupSteps: [],
};
// This function prepares the steps with paths for display in task history
// It will take in a list of task history steps,
// and return a list of task history steps with the tree built into
// the top level branch's node. So if there is a top level branch,
// after going through this function it will return an array the top level nodes
// and the first branching step. All other paths will be nested under childPaths,
// including nested branches. Normal steps will be listed as the child of the
// previous step.
export const sortStepListByPath = (task: Task): TaskHistoryStep[] => {
  const { steps } = task;
  const { pathSteps, normalSteps, pathGroupSteps } = _.reduceRight(
    steps,
    (
      obj: SortedStepObject,
      step: TaskHistoryStep,
      i: number,
      // ugh, flow doesn't understand that the fourth argument to reduceRight's
      // function is all the steps in the array ¯\_(ツ)_/¯
      // $FlowFixMe
      allSteps: TaskHistoryStep[]
    ) => {
      // it's a normal step!
      if (!isPathNode(step.node) && !isPathsGroupNode(step.node)) {
        // if it's a normal step, it's possible that it could have a branch
        // step as its child, so we need to check for those.
        const [
          pathGroupChildren,
          pathGroupNonChildren,
        ] = sortChildrenAndNonChildren(step, obj.pathGroupSteps);
        const [
          normalStepChildren,
          normalStepNonChildren,
        ] = sortChildrenAndNonChildren(step, obj.normalSteps);
        // We don't want to nest it as a child step if we're on the top level,
        // because we will show top level branches in the path node.
        return _.isEmpty([...pathGroupChildren, ...normalStepChildren]) ||
          !isStepInPath(step, allSteps)
          ? {
              ...obj,
              // be sure to add it to the beginning of the list
              // since we're moving backwards
              normalSteps: [step, ...obj.normalSteps],
            }
          : {
              ...obj,
              pathGroupSteps: pathGroupNonChildren,
              normalSteps: [
                {
                  ...step,
                  childPaths: [...normalStepChildren, ...pathGroupChildren],
                },
                ...normalStepNonChildren,
              ],
            };
      } else if (isPathNode(step.node)) {
        // if the first step in all the steps is a path node, we know that we must
        // be viewing the nodes that occurred in a path. If that's the case,
        // we don't want to nest the normal nodes as children here, but
        // rather treat it as a normal top-level node.
        const isFirstPathStep = i === 0;
        const [children, nonChildren] = sortChildrenAndNonChildren(
          step,
          obj.pathGroupSteps
        );
        return isFirstPathStep
          ? {
              ...obj,
              pathSteps: [step, ...obj.pathSteps],
            }
          : {
              normalSteps: [],
              pathGroupSteps: nonChildren,
              // make sure to add the current step before the existing step,
              // since we are moving backwards.
              pathSteps: [
                {
                  ...step,
                  childPaths: [...obj.normalSteps, ...children],
                },
                ...obj.pathSteps,
              ],
            };
      } else if (isPathsGroupNode(step.node)) {
        // when we see a path group, we need to check for the path steps
        // that are its children.
        const [children, nonChildren] = sortChildrenAndNonChildren(
          step,
          obj.pathSteps
        );
        return {
          ...obj,
          pathSteps: nonChildren,
          pathGroupSteps: [
            {
              ...step,
              childPaths: children,
            },
            ...obj.pathGroupSteps,
          ],
        };
      } else {
        // we will probably never hit this line, but flow
        // blows up without it.
        return obj;
      }
    },
    sortedStepObject
  );
  return [...pathSteps, ...normalSteps, ...pathGroupSteps];
};

export const doStepsContainPaths = (steps: TaskHistoryStep[]) => {
  return _.some(steps, step => isPathsGroupNode(step.node));
};

// similar to the above, but this can be used
// when we are not on a sub-path to check if the parent
// task as a whole contains any branch steps.
// this can be used to make sure the path dropdown menu
// is always available even if the sub path we are on doesn't
// have any path steps in it.
export const doesTaskContainPaths = (task: Task) => {
  const { zap } = task;
  const taskNodes = _.values(zap);
  return _.some(taskNodes, node => isPathsGroupNode(node));
};

export const doesStepHaveSubPaths = (step: TaskHistoryStep) => {
  const { childPaths } = step;
  if (isPathsGroupNode(step.node)) {
    return true;
  } else if (_.isEmpty(childPaths)) {
    return false;
  } else {
    // the only nodes that will have two children are path groups.
    // that would have returned true first, so we only need to check the next child now.
    return doesStepHaveSubPaths(_.first(childPaths));
  }
};

export const getNodeMapFromTask = (task: Task): NodeMap => {
  return task
    ? _.reduce(
        task.steps,
        (map, step) => {
          map[step.node.id] = step.node;
          return map;
        },
        {}
      )
    : {};
};

// find nearest ancestor that is a path step. For example If a node is anywhere
// in Path A's descendent line, this will return Path A (not the branch node)
export const findNearestPathParentStep = (
  currentStep: TaskHistoryStep,
  allSteps: TaskHistoryStep[]
): ?TaskHistoryStep => {
  const nextParentId = getParentId(currentStep.node);
  const nextParent = _.find(allSteps, step => {
    return step.node.id === nextParentId;
  });
  if (!nextParent) {
    return null;
  } else if (isPathNode(nextParent.node)) {
    return nextParent;
  } else {
    return findNearestPathParentStep(nextParent, allSteps);
  }
};

// This util expects an array of path steps, as well as a list
// of all the steps in the task. It is necessary to slice the tree
// beforehand if you only want a subset of the tree to be shown.
export const buildTaskBreadcrumbTree = (
  pathSteps: TaskHistoryStep[],
  taskSteps: TaskHistoryStep[]
) => {
  return _.reduceRight(
    pathSteps,
    (tree, step: TaskHistoryStep) => {
      // filter the child path groups whose nearest path relative match this id
      // we need to find the nearest path parent because the direct
      // parent is not necessarily the path group parent.
      const childPathSteps = _.filter(tree, pathStep => {
        const pathGroupParent = findNearestPathParentStep(pathStep, taskSteps);
        return pathGroupParent && pathGroupParent.node.id === step.node.id;
      });
      // if there are none, just add this step to the pathSteps and move on
      if (_.isEmpty(childPathSteps)) {
        return [step, ...tree];
      } else {
        // otherwise, place those children into the path step as childPaths,
        // and leave all the non-children in pathGroupSteps
        const childPathStepIds = _.map(childPathSteps, 'node.id');
        const nonChildPathSteps = _.reject(tree, pathStep => {
          return _.includes(childPathStepIds, pathStep.node.id);
        });
        return [
          {
            ...step,
            childPaths: childPathSteps,
          },
          ...nonChildPathSteps,
        ];
      }
    },
    []
  );
};

// this takes a tree of TaskHistorySteps and returns just
// the node within the step so that we have a tree of nodes.
// For use in the breadcrumb dropdown menu on the Task History pages.
export const transformStepTreeIntoNodeTree = (stepTree: TaskHistoryStep[]) => {
  return _.map(stepTree, step => {
    const { node, childPaths } = step;
    if (_.isEmpty(childPaths)) {
      return node;
    } else {
      return {
        ...node,
        childPaths: childPaths ? transformStepTreeIntoNodeTree(childPaths) : [],
      };
    }
  });
};

// First, transform the step array into a tree.
// Then, transform that tree of steps into a tree of nodes
// so it can be consumed by the dropdown menu.
export const getTaskBreadcrumbMenu = (task: Task, steps: TaskHistoryStep[]) => {
  // we will start out with all the steps in the zap,
  // but we only want the ones for which we have forks.
  const pathNodeIds = _.reduce(
    task.zap,
    (arr, node) => {
      return isPathNode(node) ? arr.concat(node.id) : arr;
    },
    []
  );
  const stepsInTask = _.filter(steps, step => {
    return _.includes(pathNodeIds, step.node.id);
  });
  const stepTree = buildTaskBreadcrumbTree(stepsInTask, steps);
  return transformStepTreeIntoNodeTree(stepTree);
};

export const getSubPathName = (task: Task, nodeId: number | ID): ?string => {
  if (!nodeId) {
    return undefined;
  }
  const pathStep = _.get(task, ['zap', `${nodeId}`]);
  if (!pathStep || !isPathNode(pathStep)) {
    return undefined;
  } else {
    return _.get(pathStep, ['params', 'name']);
  }
};

// this takes in all the tasks and goes through
// all the child tasks as well to get a cumulative number
// of all the tasks run in each path.
export const getCumulativeBillableTasks = (task: Task) => {
  if (!task.child_task_map) {
    return task ? task.billable_count : undefined;
  } else {
    return _.reduce(
      task.child_task_map,
      (total, childTask) => {
        return total + (childTask.billable_count || 0);
      },
      task.billable_count || 0
    );
  }
};

export const isSinglePathZap = (task: Task, node: SkinnyNode) => {
  const zapSortedByParent = _.sortBy(task.zap, step => {
    return step.parent;
  });
  const pathSteps = _.filter(zapSortedByParent, isPathNode);
  const pathStepsByParent = _.reduce(
    pathSteps,
    (obj, step) => {
      if (step.parent && !obj[step.parent]) {
        obj[step.parent] = [step];
        return obj;
      } else {
        return {
          ...obj,
          [step.parent]: [...obj[step.parent], step],
        };
      }
    },
    {}
  );
  const nearestPathParent = isPathsGroupNode(node)
    ? node
    : findNodeUpwards(task.zap, node.id, isPathsGroupNode);
  return !nearestPathParent
    ? false
    : _.get(pathStepsByParent, [nearestPathParent.id], []).length === 1;
};

// local functions for use only in getStepsForTask
const findNodeInSteps = (
  steps: Array<TaskHistoryStep>,
  nodeId: ID | number
) => {
  const step = _.find(steps, stp => Number(stp.node.id) === Number(nodeId));
  return step ? step.node : undefined;
};

const trimStepsUntilNode = (
  steps: Array<TaskHistoryStep>,
  nodeId: ID | number
) => _.dropWhile(steps, step => Number(step.node.id) !== Number(nodeId));

export const getStepsForTask = ({
  task,
  pathId,
  nodeId,
}: {
  task: Task,
  pathId?: ID,
  nodeId?: ID | number,
}): Array<TaskHistoryStep> => {
  const allSteps = getTaskDetailedSteps(task, pathId, nodeId);
  const node = nodeId ? findNodeInSteps(allSteps, nodeId) : undefined;
  const isSinglePath = node && isSinglePathZap(task, node);
  if (isSinglePath && nodeId) {
    return trimStepsUntilNode(allSteps, nodeId);
  } else if (pathId) {
    const taskIdNodeMap = getTaskIdToNodeIdMap(task);
    const startingNodeId = taskIdNodeMap[pathId];
    return trimStepsUntilNode(allSteps, startingNodeId);
  } else {
    return allSteps;
  }
};
